import React, { useEffect, useMemo, useRef } from "react";
import { Studio, DEFAULT_LOOK } from "../../video/studio";
import { demoSpeech } from "../../video/lipsync";
import { buildPresenter } from "../../video/avatar";
import { PREVIEW } from "../VideoStudioPanel";

const LOOP_SECONDS = 6.4;
const LOOK = { ...DEFAULT_LOOK, grade: "none", vignette: 0.18, grain: 0, captions: "off", letterbox: false, watermark: "", title: "", outro: "" };

// The presenter as the video will show them, alive: blinking, moving, and - when `talking` - speaking an invented sentence so the lips can be seen.
// It is drawn by the same studio that makes the video, so what is seen here is what is saved.
export default function LivePreview({ avatar, photo = null, background = null, ratio = "16:9", talking = true, className = "" }) {
  const canvasRef = useRef(null);
  const studioRef = useRef(null);
  const timeRef = useRef(0);
  const demo = useMemo(() => demoSpeech(LOOP_SECONDS), []);

  const presenter = useMemo(
    () => buildPresenter(avatar, { img: photo?.img || null, hasAlpha: !!photo?.hasAlpha, bg: background?.img || null, seed: 7 }),
    [avatar, photo, background],
  );
  const scene = useMemo(() => ({ text: "", seconds: LOOP_SECONDS, transition: "cut", presenter, analysis: talking ? demo : null }), [presenter, demo, talking]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    if (!studioRef.current) studioRef.current = new Studio(canvas, [scene], LOOK, PREVIEW[ratio] || PREVIEW["16:9"]);
    else {
      const studio = studioRef.current;
      const [w, h] = PREVIEW[ratio] || PREVIEW["16:9"];
      if (studio.W !== w || studio.H !== h) studio.setSize([w, h]);
      studio.setScenes([scene]);
    }
    const started = performance.now();
    const id = setInterval(() => {
      if (document.hidden) return;
      timeRef.current = (performance.now() - started) / 1000;
      studioRef.current.drawFrame(timeRef.current % LOOP_SECONDS, true);
    }, 33);
    return () => clearInterval(id);
  }, [scene, ratio]);

  useEffect(() => () => { studioRef.current = null; }, []);

  return <canvas ref={canvasRef} className={`block w-full h-auto rounded-xl bg-black ${className}`} style={{ aspectRatio: ratio.replace(":", " / "), maxHeight: ratio === "9:16" ? 560 : undefined, margin: "0 auto" }} aria-label="Preview of the presenter" />;
}
